import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Dialog } from '@mui/material';

import { uuid } from '../../../utils/stringUtils';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';

import { validate } from './validateJobGoals';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const TITLE_MAX_LENGTH = 100;

const EditJobGoal = ({
    jobGoal,
    participantId,
    jobSectors,
    isDisabled = false,
    isOpen,
    onCancel,
    onUpdate,
    onAdd,
    editType
}) => {
    // LOCAL STATE
    const initialState = {
        id: `new-${uuid()}`,
        title: '',
        sectorId: '',
        sector: '',
        participantId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [type, setType] = useState('edit');
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // USEEFFECTS
    useEffect(() => {
        setType(editType);
        if (editType === 'edit') {
            setNewEntry(jobGoal);
            setInitialStateOnEntry(jobGoal);
        } else setNewEntry((prev) => ({ ...prev, participantId }));
    }, [editType, jobGoal]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        key in errors && setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENTHANDLERS
    const onClose = () => {
        setNewEntry(initialState);
        setErrors({});
        onCancel();
    };

    const onUpdateSector = (sectorId) => {
        clearError('sectorId');
        const sector = jobSectors.find((el) => el.id === sectorId)?.name || '';
        setNewEntry((prev) => ({ ...prev, sectorId, sector }));
    };

    const onUpdateTitle = (title) => {
        clearError('title');
        setNewEntry((prev) => ({ ...prev, title }));
    };

    const onEdit = () => {
        const newErrors = validate(newEntry, initialStateOnEntry, type);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        type === 'create' ? onAdd(newEntry) : onUpdate(newEntry);
        setNewEntry(initialState);
        setErrors({});
    };

    // RENDER
    return (
        <Dialog open={isOpen} onClose={onClose} fullWidth aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader} id="jobgoal-modal-title">
                <h3>{type === 'create' ? 'Add' : 'Edit'} Job Goal</h3>
            </div>
            <div className={modal.modalContent}>
                <div className={form.formWrapper} data-testid="job-goal-edit-form">
                    <SingleSelect
                        id="sectorId"
                        label="Job Sector"
                        placeholder="Select a job sector..."
                        mandatory={true}
                        disabled={isDisabled}
                        menuItems={jobSectors}
                        selectedId={newEntry.sectorId}
                        selected={jobSectors.find((el) => el.id === newEntry.sectorId)}
                        error={errors.sectorId}
                        onChange={(id) => onUpdateSector(id)}
                    />
                    <TextInputField
                        id="title"
                        disabled={isDisabled}
                        label="Job Title"
                        maxLength={TITLE_MAX_LENGTH}
                        value={newEntry.title || ''}
                        placeholder="Add a title..."
                        error={errors.title}
                        onChange={(e) => onUpdateTitle(e.target.value)}
                    />
                    <div className={modal.actionButtons}>
                        <Button
                            id="jobGoalEditButton"
                            disabled={isDisabled || isUpdateButtonDisabled}
                            content={type === 'edit' ? 'Update' : 'Add'}
                            error={errors.button}
                            customErrorClass="formActionsError"
                            clearError={() => clearError('button')}
                            onClick={onEdit}
                        />
                        <Button
                            id="jobGoalCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onClose}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

EditJobGoal.propTypes = {
    jobGoal: PropTypes.object,
    participantId: PropTypes.string,
    isDisabled: PropTypes.bool,
    jobSectors: PropTypes.array,
    editType: PropTypes.string,
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func,
    onUpdate: PropTypes.func,
    onAdd: PropTypes.func
};
export default EditJobGoal;
